import { Pencil, Plus, Eye } from 'lucide-react';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import type { SkillTreeNodeData } from './skill-framework-chart-utils';
import { normalizeSkillDescription } from './skill-framework-chart-utils';
import SkillFrameworkActions from './skill-framework-hierarchy-actions';

interface SkillFrameworkHierarchySheetProps {
    isOpen: boolean;
    depth: number;
    node: SkillTreeNodeData;
    getSkillTypeLabel: (id?: number | null) => string;
    isStudent: boolean;
    canManage: boolean;
    isPublished: boolean;
    isPublishing: boolean;
    onPublish: () => void;
    onCreateChild: (nodeId: string) => void;
    onEdit: (nodeId: string) => void;
    isDuplicating: boolean;
    onDuplicate: (nodeId: string) => void;
    isDeleting: boolean;
    onDelete: (nodeId: string) => void;
    rootId: string | number;
    onClose: () => void;
    onView: (nodeId: string) => void;
}

const SkillFrameworkHierarchySheet = ({
    isOpen,
    depth,
    node,
    getSkillTypeLabel,
    isStudent,
    canManage,
    isPublished,
    isPublishing,
    onPublish,
    onCreateChild,
    onEdit,
    isDuplicating,
    onDuplicate,
    isDeleting,
    onDelete,
    rootId,
    onClose,
    onView,
}: SkillFrameworkHierarchySheetProps) => {
    const width = depth === 0 ? 360 : 340;
    const topOffset = 0;
    const description = normalizeSkillDescription(node.description);

    return (
        <Sheet open={isOpen} onOpenChange={(open) => (!open ? onClose() : null)}>
            <SheetContent
                side="right"
                hideOverlay
                className="bg-background p-0"
                style={{
                    width,
                    maxWidth: '90vw',
                    top: `${topOffset}px`,
                }}
            >
                <SheetHeader className="border-b border-border pt-8 relative">
                    <SheetTitle className="text-base">
                        {node.name}
                    </SheetTitle>
                    <div className="flex items-start gap-2 mt-1">
                        <div className="min-w-0 flex-1">
                            {description ? (
                                <SheetDescription className="text-xs">
                                    {description}
                                </SheetDescription>
                            ) : (
                                <p className="text-xs text-muted-foreground italic">
                                    No description added
                                </p>
                            )}
                        </div>
                        {canManage && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0"
                                onClick={() => onEdit(String(node.id))}
                            >
                                <Pencil className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto">
                    <div className="px-4 py-3 space-y-3">
                    <div className="flex items-center justify-between border-b border-border/60 pb-2">
                        <p className="text-sm font-medium text-muted-foreground">
                            Sub categories
                        </p>
                        {canManage && (
                            <button
                                type="button"
                                className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
                                onClick={() => onCreateChild(node.id)}
                            >
                                <Plus className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                        {node.children?.length ? (
                            node.children.map((child) => (
                                <div
                                    key={child.id}
                                    className="flex items-start justify-between gap-2 border-b border-border/60 pb-2"
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium truncate">
                                            {child.name}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {getSkillTypeLabel(child.skillTypeId)}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {(isStudent || canManage) && (
                                            <button
                                                type="button"
                                                className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
                                                onClick={() => onView(child.id)}
                                            >
                                                <Eye className="h-4 w-4" />
                                            </button>
                                        )}
                                            {canManage && (
                                            <>
                                                <button
                                                    type="button"
                                                    className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent"
                                                    onClick={() => onCreateChild(child.id)}
                                                >
                                                    <Plus className="h-4 w-4" />
                                                </button>
                                                <SkillFrameworkActions
                                                    isRoot={String(child.id) === String(rootId)}
                                                    isPublished={isPublished}
                                                    isPublishing={isPublishing}
                                                    onPublish={onPublish}
                                                    onEdit={onEdit}
                                                    isDuplicating={isDuplicating}
                                                    onDuplicate={onDuplicate}
                                                    isDeleting={isDeleting}
                                                    onDelete={onDelete}
                                                    nodeId={String(child.id)}
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                No sub-categories available.
                            </p>
                        )}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default SkillFrameworkHierarchySheet;
